import type { SpinnerProps } from './Spinner';
import type { SpinnerSize } from '@natter-co/design-system';

type DsProps = {
  size: SpinnerSize;
  inheritColor: boolean;
  className?: string;
  ariaLabel?: string;
  testId?: string;
};

/** The only MUI `size` values that are exactly a DS size, so swapping the ring moves nothing around it. */
const SIZES: Record<number, SpinnerSize> = { 16: 'xs', 20: 'sm', 24: 'md', 32: 'lg', 40: 'xl' };

const MUI_DEFAULT_SIZE = 40;

const toPixels = (size: SpinnerProps['size']): number | null => {
  if (size === undefined) return MUI_DEFAULT_SIZE;
  if (typeof size === 'number') return size;
  const match = /^(\d+(?:\.\d+)?)px$/.exec(size);
  return match ? Number(match[1]) : null;
};

/**
 * Maps the legacy MUI `CircularProgress` props onto the Design System 2.0 `Spinner`.
 *
 * TODO(HNS-264): temporary, like the other `toDsProps` mappers; retiring the flag deletes it.
 *
 * `null` sends the caller back to MUI. Cases that stay on MUI:
 *   - a `size` that is not 16, 20, 24, 32 or 40 (12, 14, 18, 50...): the DS spinner has five sizes and
 *     rounding to the nearest would move whatever sits beside it;
 *   - `sx`, `style`, `thickness`, `value`, `variant`, `aria-hidden`: the DS spinner is a status that
 *     always announces, has one stroke, and cannot be sized or coloured from outside except by class;
 *   - a `color` other than the default and `inherit`;
 *   - any other prop the DS spinner would accept and silently drop.
 *
 * `color="inherit"` is kept by a `text-inherit` class, so a spinner on a coloured surface still takes
 * that surface's text colour. The default colour is not kept: MUI drew the accent blue, and the DS
 * spinner is the secondary foreground by design, because the accent marks wayfinding and never status.
 *
 * `disableShrink` only changes how MUI animates its own arc, so it is dropped.
 */
export const toDsProps = ({
  size,
  color,
  disableShrink: _disableShrink,
  className,
  'aria-label': ariaLabel,
  'data-testid': testId,
  ...unsupported
}: SpinnerProps): DsProps | null => {
  if (Object.keys(unsupported).length > 0) return null;
  if (color !== undefined && color !== 'primary' && color !== 'inherit') return null;

  const pixels = toPixels(size);
  const dsSize = pixels === null ? undefined : SIZES[pixels];
  if (!dsSize) return null;

  return { size: dsSize, inheritColor: color === 'inherit', className, ariaLabel, testId };
};
